import { useState, useEffect, useRef } from 'react';

export default function Navigation() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const [isLocationsOpen, setIsLocationsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileIndustriesOpen, setMobileIndustriesOpen] = useState(false);
  const [mobileLocationsOpen, setMobileLocationsOpen] = useState(false);

  const servicesRef = useRef<HTMLDivElement>(null);
  const industriesRef = useRef<HTMLDivElement>(null);
  const locationsRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target as Node)) {
        setServicesOpen(false);
      }
      if (industriesRef.current && !industriesRef.current.contains(event.target as Node)) {
        setIndustriesOpen(false);
      }
      if (locationsRef.current && !locationsRef.current.contains(event.target as Node)) {
        setIsLocationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close other dropdowns when one opens
  const handleServicesOpen = (isOpen: boolean) => {
    setServicesOpen(isOpen);
    if (isOpen) {
      setIndustriesOpen(false);
      setIsLocationsOpen(false);
    }
  };

  const handleIndustriesOpen = (isOpen: boolean) => {
    setIndustriesOpen(isOpen);
    if (isOpen) {
      setServicesOpen(false);
      setIsLocationsOpen(false);
    }
  };

  const handleLocationsOpen = (isOpen: boolean) => {
    setIsLocationsOpen(isOpen);
    if (isOpen) {
      setServicesOpen(false);
      setIndustriesOpen(false);
    }
  };

  const serviceLinks = [
    { title: 'Web Design', href: '/services/web-design', icon: 'ri-layout-grid-line' },
    { title: 'AI Integration', href: '/services/ai-integration', icon: 'ri-robot-2-line' },
    { title: 'SEO', href: '/services/seo', icon: 'ri-search-line' },
    { title: 'Social Media', href: '/services/social-media', icon: 'ri-megaphone-line' },
    { title: 'Google LSA + PPC', href: '/services/google-ads', icon: 'ri-google-line' },
    { title: 'Reputation Management', href: '/services/reputation-management', icon: 'ri-star-smile-line' }
  ];

  const industryLinks = [
    { title: 'Electrical', href: '/industries/electrical', icon: 'ri-flashlight-line' },
    { title: 'HVAC', href: '/industries/hvac', icon: 'ri-temp-cold-line' },
    { title: 'Landscaping', href: '/industries/landscaping', icon: 'ri-plant-line' },
    { title: 'Pest Control', href: '/industries/pest-control', icon: 'ri-bug-line' },
    { title: 'Plumbing', href: '/industries/plumbing', icon: 'ri-drop-line' },
    { title: 'Roofing', href: '/industries/roofing', icon: 'ri-home-4-line' }
  ];

  const locationLinks = [
    { title: 'New York', href: '/locations/new-york', icon: 'ri-map-pin-fill' },
    { title: 'Los Angeles', href: '/locations/los-angeles', icon: 'ri-map-pin-fill' },
    { title: 'Miami', href: '/locations/miami', icon: 'ri-map-pin-fill' },
    { title: 'Houston', href: '/locations/houston', icon: 'ri-map-pin-fill' },
    { title: 'Phoenix', href: '/locations/phoenix', icon: 'ri-map-pin-fill' },
    { title: 'Philadelphia', href: '/locations/philadelphia', icon: 'ri-map-pin-fill' },
    { title: 'San Diego', href: '/locations/san-diego', icon: 'ri-map-pin-fill' },
    { title: 'Dallas', href: '/locations/dallas', icon: 'ri-map-pin-fill' },
    { title: 'Atlanta', href: '/locations/atlanta', icon: 'ri-map-pin-fill' },
    { title: 'Orlando', href: '/locations/orlando', icon: 'ri-map-pin-fill' },
    { title: 'Boca Raton', href: '/locations/boca-raton', icon: 'ri-map-pin-fill' },
    { title: 'Fort Lauderdale', href: '/locations/fort-lauderdale', icon: 'ri-map-pin-fill' }
  ];

  const scrollToSection = (sectionId: string) => {
    // Check if we're on the homepage
    if (window.location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setMobileMenuOpen(false);
      }
    } else {
      // Navigate to homepage with hash
      window.location.href = `/#${sectionId}`;
    }
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            {/* Logo */}
            <div className="flex-shrink-0">
              <a href="/" className="block cursor-pointer hover:opacity-80 transition-opacity">
                <img 
                  src="https://static.readdy.ai/image/4bccfbc3b25f334801db453be76bfe26/d54a0cb9b970514e9b304074d19e0d5b.png" 
                  alt="Million Dollar Contractors" 
                  className="h-16 w-auto"
                />
              </a>
            </div>

            {/* Navigation Links */}
            <div className="hidden lg:flex items-center space-x-8">
              <a 
                href="/"
                className="text-white hover:text-red-500 transition-colors font-medium cursor-pointer whitespace-nowrap"
              >
                Home
              </a>

              {/* Services Dropdown */}
              <div 
                ref={servicesRef}
                className="relative"
                onMouseEnter={() => handleServicesOpen(true)}
              >
                <button
                  onClick={() => handleServicesOpen(!servicesOpen)}
                  className="text-white hover:text-red-500 transition-colors font-medium cursor-pointer whitespace-nowrap flex items-center gap-1"
                >
                  Services
                  <i className={`ri-arrow-down-s-line transition-transform ${servicesOpen ? 'rotate-180' : ''}`}></i>
                </button>
                
                {servicesOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-black rounded-xl shadow-xl py-2 border border-white/10">
                    {serviceLinks.map((service, index) => (
                      <a
                        key={index}
                        href={service.href}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-all cursor-pointer"
                      >
                        <i className={`${service.icon} text-lg text-red-500`}></i>
                        <span className="text-white font-medium">{service.title}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Industries Dropdown */}
              <div 
                ref={industriesRef}
                className="relative"
                onMouseEnter={() => handleIndustriesOpen(true)}
              >
                <button
                  onClick={() => handleIndustriesOpen(!industriesOpen)}
                  className="text-white hover:text-red-500 transition-colors font-medium cursor-pointer whitespace-nowrap flex items-center gap-1"
                >
                  Industries
                  <i className={`ri-arrow-down-s-line transition-transform ${industriesOpen ? 'rotate-180' : ''}`}></i>
                </button>
                
                {industriesOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-black rounded-xl shadow-xl py-2 border border-white/10">
                    {industryLinks.map((industry, index) => (
                      <a
                        key={index}
                        href={industry.href}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-all cursor-pointer"
                      >
                        <i className={`${industry.icon} text-lg text-red-500`}></i>
                        <span className="text-white font-medium">{industry.title}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Locations Dropdown */}
              <div
                ref={locationsRef}
                className="relative"
                onMouseEnter={() => handleLocationsOpen(true)}
              >
                <button
                  onClick={() => handleLocationsOpen(!isLocationsOpen)}
                  className="text-white hover:text-red-500 transition-colors font-medium cursor-pointer whitespace-nowrap flex items-center gap-1"
                >
                  Locations
                  <i className={`ri-arrow-down-s-line transition-transform ${isLocationsOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {isLocationsOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-black rounded-xl shadow-xl py-2 border border-white/10 max-h-96 overflow-y-auto">
                    <button
                      onClick={() => scrollToSection('areas-we-serve')}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-all cursor-pointer w-full text-left border-b border-white/10"
                    >
                      <i className="ri-map-2-line text-lg text-red-500"></i>
                      <span className="text-white font-semibold">View All Areas</span>
                    </button>
                    {locationLinks.map((location, index) => (
                      <a
                        key={index}
                        href={location.href}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-all cursor-pointer"
                      >
                        <i className={`${location.icon} text-lg text-red-500`}></i>
                        <span className="text-white font-medium">{location.title}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <a 
                href="/about"
                className="text-white hover:text-red-500 transition-colors font-medium cursor-pointer whitespace-nowrap"
              >
                About
              </a>
            </div>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <button 
                onClick={() => scrollToSection('contact')}
                className="inline-flex items-center px-6 py-3 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-all cursor-pointer whitespace-nowrap"
              >
                Get Started
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
            </div>

            {/* Mobile Menu Button & Phone Icon */}
            <div className="lg:hidden flex items-center gap-3">
              <a 
                href="tel:2153963311"
                className="text-white p-2 cursor-pointer hover:text-red-500 transition-colors"
              >
                <i className="ri-phone-line text-2xl"></i>
              </a>
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-white p-2 cursor-pointer"
              >
                <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)}></div>
          <div className="fixed top-24 left-0 right-0 bottom-0 bg-black overflow-y-auto">
            <div className="px-4 py-6 space-y-4">
              {/* Phone Number */}
              <a 
                href="tel:2153963311"
                className="flex items-center gap-3 px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors cursor-pointer"
              >
                <i className="ri-phone-line text-xl"></i>
                <span className="font-semibold">(215) 396-3311</span>
              </a>

              {/* Get Started Button */}
              <button 
                onClick={() => scrollToSection('contact')}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap border border-white/20 w-full"
              >
                Get Started
                <i className="ri-arrow-right-line"></i>
              </button>

              {/* Home */}
              <a 
                href="/"
                className="block px-4 py-3 text-white hover:bg-white/5 rounded-lg cursor-pointer font-medium"
              >
                Home
              </a>

              {/* Services Accordion */}
              <div className="border-t border-white/10 pt-4">
                <button
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  className="flex items-center justify-between w-full px-4 py-3 text-white hover:bg-white/5 rounded-lg cursor-pointer font-medium"
                >
                  <span>Services</span>
                  <i className={`ri-arrow-down-s-line transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {mobileServicesOpen && (
                  <div className="mt-2 space-y-1 pl-4">
                    {serviceLinks.map((service, index) => (
                      <a
                        key={index}
                        href={service.href}
                        className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-white/5 rounded-lg cursor-pointer"
                      >
                        <i className={`${service.icon} text-red-500`}></i>
                        <span>{service.title}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Industries Accordion */}
              <div className="border-t border-white/10 pt-4">
                <button
                  onClick={() => setMobileIndustriesOpen(!mobileIndustriesOpen)}
                  className="flex items-center justify-between w-full px-4 py-3 text-white hover:bg-white/5 rounded-lg cursor-pointer font-medium"
                >
                  <span>Industries</span>
                  <i className={`ri-arrow-down-s-line transition-transform ${mobileIndustriesOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {mobileIndustriesOpen && (
                  <div className="mt-2 space-y-1 pl-4">
                    {industryLinks.map((industry, index) => (
                      <a
                        key={index}
                        href={industry.href}
                        className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-white/5 rounded-lg cursor-pointer"
                      >
                        <i className={`${industry.icon} text-red-500`}></i>
                        <span>{industry.title}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Locations Accordion */}
              <div className="border-t border-white/10 pt-4">
                <button
                  onClick={() => setMobileLocationsOpen(!mobileLocationsOpen)}
                  className="flex items-center justify-between w-full px-4 py-3 text-white hover:bg-white/5 rounded-lg cursor-pointer font-medium"
                >
                  <span>Locations</span>
                  <i className={`ri-arrow-down-s-line transition-transform ${mobileLocationsOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {mobileLocationsOpen && (
                  <div className="mt-2 space-y-1 pl-4">
                    <button
                      onClick={() => scrollToSection('areas-we-serve')}
                      className="flex items-center gap-3 px-4 py-3 text-white hover:bg-white/5 rounded-lg cursor-pointer w-full text-left border-b border-white/10 mb-2"
                    >
                      <i className="ri-map-2-line text-red-500"></i>
                      <span className="font-semibold">View All Areas</span>
                    </button>
                    {locationLinks.map((location, index) => (
                      <a
                        key={index}
                        href={location.href}
                        className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-white/5 rounded-lg cursor-pointer"
                      >
                        <i className={`${location.icon} text-red-500`}></i>
                        <span>{location.title}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* About */}
              <a 
                href="/about"
                className="block px-4 py-3 text-white hover:bg-white/5 rounded-lg cursor-pointer font-medium w-full text-left"
              >
                About
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}